/**
 * Kutlama sesi — CLAUDE.md §7.8.
 *
 * NEDEN BÖYLE: Faz 13'te iPhone üzerinde ölçtük — iOS'ta bildirime
 * özel ses VERİLEMİYOR (`sound` seçeneği WebKit'te hiç yok) ve
 * `navigator.vibrate` de bulunmuyor. Geriye tek bir yol kalıyor:
 * uygulama AÇIKKEN sesi kendimiz çalmak. Bu dosya onu yapıyor.
 *
 * Ses bir dosyadan gelmiyor, burada üretiliyor (WebAudio osilatörleri).
 * Böylece depoya ikili bir ses dosyası eklemek, onu indirmek ve
 * önbelleklemek gerekmiyor — birkaç satır matematik yetiyor.
 */

/** Kutlama motifi: Do–Mi–Sol–Do (yükselen majör arpej), Hz cinsinden. */
const FANFARE_NOTES = [523.25, 659.25, 783.99, 1046.5];

/** Notalar arasındaki gecikme (saniye). */
const NOTE_INTERVAL = 0.11;

/** Tek bir notanın sönme süresi (saniye). */
const NOTE_DECAY = 0.55;

let audioContext: AudioContext | null = null;

/**
 * Bekleyen bir kutlama sesi var mı?
 *
 * iOS, kullanıcı ekrana dokunmadan ses çalmayı engeller. Gün kapanışı
 * ise uygulama daha yeni açılırken çalışır — yani o anda henüz bir
 * dokunuş olmamıştır. Böyle bir durumda sesi atmıyoruz, bir kenara
 * koyuyoruz ve kullanıcının ilk dokunuşunda çalıyoruz.
 */
let hasPendingSound = false;

/**
 * AudioContext'i (gerekirse) oluşturur.
 *
 * Safari uzun süre yalnızca `webkitAudioContext` adını tanıdı; eski
 * iOS sürümlerinde de çalışsın diye ikisini de deniyoruz.
 */
function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;

  const legacyWindow = window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextCtor = window.AudioContext ?? legacyWindow.webkitAudioContext;
  if (!AudioContextCtor) return null;

  audioContext = new AudioContextCtor();
  return audioContext;
}

/**
 * Arpeji ses kartına zamanlar.
 *
 * Her nota kendi osilatörünü ve ses seviyesi zarfını alır: hızlı
 * yükselip yavaşça sönerler, böylece "biip" değil, çalınmış bir nota
 * gibi duyulur.
 */
function scheduleFanfare(context: AudioContext): void {
  const startTime = context.currentTime;

  FANFARE_NOTES.forEach((frequency, index) => {
    const noteTime = startTime + index * NOTE_INTERVAL;

    const oscillator = context.createOscillator();
    // Üçgen dalga, saf sinüsten daha parlak ama kare dalga kadar sert
    // değil — kutlama için uygun ton.
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;

    const envelope = context.createGain();
    envelope.gain.setValueAtTime(0, noteTime);
    envelope.gain.linearRampToValueAtTime(0.22, noteTime + 0.02);
    // Üstel sönme kulağa doğal gelir; hedef tam 0 olamaz (üstel eğri
    // sıfıra inemez), o yüzden duyulmaz bir değere iniyoruz.
    envelope.gain.exponentialRampToValueAtTime(0.0001, noteTime + NOTE_DECAY);

    oscillator.connect(envelope).connect(context.destination);
    oscillator.start(noteTime);
    oscillator.stop(noteTime + NOTE_DECAY + 0.05);
  });
}

/**
 * Kutlama sesini çalar; tarayıcı izin vermiyorsa ilk dokunuşa erteler.
 *
 * Hiçbir durumda hata fırlatmaz — ses çalamamak, kutlamayı
 * göstermemek için bir sebep değil.
 */
export async function playCelebrationSound(): Promise<void> {
  const context = getAudioContext();
  if (!context) return;

  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      // Dokunuş olmadan açılmadı; aşağıda ertelenecek.
    }
  }

  if (context.state === 'running') {
    scheduleFanfare(context);
    return;
  }

  hasPendingSound = true;
}

/**
 * Kullanıcının ilk dokunuşunda ses iznini açar.
 *
 * Uygulama açılışında BİR KEZ çağrılır. Bekleyen bir kutlama sesi
 * varsa o dokunuşta çalınır; yoksa sadece ses kanalı açılmış olur ve
 * sonraki kutlamalar anında duyulur.
 */
export function armAudioUnlock(): void {
  const unlock = async (): Promise<void> => {
    const context = getAudioContext();
    if (!context) return;

    try {
      await context.resume();
    } catch {
      return;
    }
    if (context.state !== 'running') return;

    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('touchend', handler);

    if (hasPendingSound) {
      hasPendingSound = false;
      scheduleFanfare(context);
    }
  };

  // Dinleyiciyi kaldırabilmek için aynı fonksiyon referansı gerekli.
  const handler = (): void => {
    void unlock();
  };

  window.addEventListener('pointerdown', handler);
  window.addEventListener('touchend', handler);
}
